import type { SettingsStatusTone } from "./therapist-settings-status";

export type AdminStatusSectionKey =
  | "platform"
  | "pricing"
  | "billing"
  | "therapist-onboarding"
  | "communication";

export interface AdminSettingsStatusInput {
  multiplierCount: number;
  questionnaireQuestionCount: number;
  specializationCount: number;
  emptyEmailTemplateCount: number;
  billingConfigured: boolean;
  platformMissingFields: string[];
}

export interface AdminSectionStatus {
  key: AdminStatusSectionKey;
  title: string;
  tone: SettingsStatusTone;
  label: string;
  detail: string;
}

export interface AdminSettingsStatusResult {
  sections: Record<AdminStatusSectionKey, AdminSectionStatus>;
  warningCount: number;
  warningTitles: string[];
}

export function computeAdminSettingsStatus(
  input: AdminSettingsStatusInput,
): AdminSettingsStatusResult {
  const platformWarning = input.platformMissingFields.length > 0;
  const onboardingWarning =
    input.questionnaireQuestionCount < 1 || input.specializationCount < 1;

  const sections: Record<AdminStatusSectionKey, AdminSectionStatus> = {
    platform: {
      key: "platform",
      title: "Platforma",
      tone: platformWarning ? "warning" : "ready",
      label: platformWarning ? "Nedostaju osnovni podaci" : "Konfigurisano",
      detail: platformWarning
        ? `Popuni: ${input.platformMissingFields.slice(0, 3).join(", ")}`
        : "Osnovna pravila i bankovni podaci su popunjeni.",
    },
    pricing: {
      key: "pricing",
      title: "Cijene",
      tone: input.multiplierCount < 1 ? "warning" : "ready",
      label: input.multiplierCount < 1 ? "Nema multiplikatora" : "Konfigurisano",
      detail:
        input.multiplierCount < 1
          ? "Rezervacije ne mogu izračunati regionalnu cijenu."
          : `${input.multiplierCount} regija sa multiplikatorom.`,
    },
    billing: {
      key: "billing",
      title: "Naplata",
      tone: input.billingConfigured ? "ready" : "warning",
      label: input.billingConfigured ? "Konfigurisano" : "Nepotpuna konfiguracija",
      detail: input.billingConfigured
        ? "Cijene pretplate i podaci izdavaoca su popunjeni."
        : "Dopuni cijene pretplate i podatke izdavaoca računa.",
    },
    "therapist-onboarding": {
      key: "therapist-onboarding",
      title: "Onboarding",
      tone: onboardingWarning ? "warning" : "ready",
      label: onboardingWarning ? "Nepotpun onboarding" : "Konfigurisano",
      detail: onboardingWarning
        ? "Dodaj pitanja upitnika i specijalizacije prije enrollmenta."
        : `${input.questionnaireQuestionCount} pitanja · ${input.specializationCount} specijalizacija.`,
    },
    communication: {
      key: "communication",
      title: "Komunikacija",
      tone: input.emptyEmailTemplateCount > 0 ? "warning" : "ready",
      label: input.emptyEmailTemplateCount > 0 ? "Prazni šabloni" : "Konfigurisano",
      detail:
        input.emptyEmailTemplateCount > 0
          ? `${input.emptyEmailTemplateCount} email šablona je prazno.`
          : "Svi email šabloni imaju sadržaj.",
    },
  };

  const warningSections = Object.values(sections).filter((s) => s.tone === "warning");

  return {
    sections,
    warningCount: warningSections.length,
    warningTitles: warningSections.map((s) => s.title),
  };
}
